import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound } from 'lucide-react'
import { accessApi, type Role, type RoleInput } from '@/api/accessApi'
import { ListState } from '@/components/admin/AdminShared'
import { TextAction } from '@/components/admin/catalog/CatalogShared'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { EmptyState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { useAccessMutation, useRoles } from '@/hooks/useAccess'
import { cn } from '@/lib/utils'
import { RoleDialog } from './RoleDialog'

/** Roles: built-in ones read-only, custom ones editable (roles.manage). */
export function RolesTab() {
  const { t } = useTranslation()
  const roles = useRoles()
  const [open, setOpen] = useState<Role | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Role | null>(null)
  const create = useAccessMutation(accessApi.createRole)
  const update = useAccessMutation(accessApi.updateRole)
  const remove = useAccessMutation(accessApi.deleteRole)

  const close = () => {
    setOpen(null)
    create.reset()
    update.reset()
  }
  const submit = (input: RoleInput) => {
    if (open && open !== 'new') update.mutate({ ...input, id: open.id }, { onSuccess: close })
    else create.mutate(input, { onSuccess: close })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setOpen('new')}>{t('access.roles.new')}</Button>
      </div>
      <ListState query={roles}>
        {(roles.data ?? []).length === 0 ? (
          <EmptyState compact icon={KeyRound} message={t('access.roles.empty')} />
        ) : (
          <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label={t('access.tabs.roles')}>
            {(roles.data ?? []).map((r) => (
              <li key={r.id} className="flex flex-col gap-3 rounded-lg border border-line bg-background p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-body font-semibold text-ink-strong">{r.displayName}</p>
                    <p className="truncate font-mono text-label text-ink-muted">{r.slug}</p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-sm px-2 py-[3px] text-label font-medium',
                      r.isSystem ? 'bg-surface-subtle text-ink-body' : 'bg-accent text-accent-foreground',
                    )}
                  >
                    {r.isSystem ? t('access.roles.builtIn') : t('access.roles.custom')}
                  </span>
                </div>
                <p className="text-meta text-ink-body">
                  {[
                    t(`access.kindsPlural.${r.kind}`),
                    r.fullAccess ? t('access.roles.fullAccess') : t('access.roles.permissions', { count: r.permissions.length }),
                    t('access.roles.users', { count: r._count.users }),
                    r.scopes.length ? t('access.roles.scoped') : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <div className="mt-auto flex gap-3 border-t border-line pt-3">
                  <TextAction onClick={() => setOpen(r)}>{r.isSystem ? t('access.roles.view') : t('access.roles.edit')}</TextAction>
                  {r.isSystem ? null : (
                    <span title={r._count.users > 0 ? t('access.roles.inUse') : undefined}>
                      <TextAction danger disabled={r._count.users > 0} onClick={() => setDeleting(r)}>
                        {t('access.roles.delete')}
                      </TextAction>
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </ListState>

      <RoleDialog
        open={open !== null}
        role={open === 'new' ? null : open}
        pending={create.isPending || update.isPending}
        error={create.error ?? update.error}
        onOpenChange={(o) => (o ? undefined : close())}
        onSubmit={submit}
      />
      <ConfirmDialog
        open={deleting !== null}
        title={t('access.roles.deleteTitle', { name: deleting?.displayName ?? '' })}
        body={t('access.roles.deleteBody')}
        confirmLabel={t('access.roles.delete')}
        destructive
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : null}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null)
            remove.reset()
          }
        }}
      />
    </div>
  )
}
